import type { FeatureFlagId, WorkspaceFeatureFlags } from '@/external/db/types';

// Pure feature-flag registry + resolution, shared by the client provider
// (feature-flags.tsx) and server code (the back-office route). No React, no
// 'use client', so it is safe to import from a route handler. The id union and
// the WorkspaceFeatureFlags shape live in external/db/types (they type the
// workspace_feature_flags.flags jsonb column).

export type { FeatureFlagId, WorkspaceFeatureFlags };

export interface FeatureFlagDef {
  id: FeatureFlagId;
  label: string;
  description: string;
  defaultOn: boolean;
  // When true, the flag defaults OFF in the KPC workspace even though defaultOn
  // is true elsewhere. A workspace override still wins.
  hiddenByDefaultInKpc: boolean;
  // When true, the flag defaults ON in the KPC workspace even though defaultOn
  // is false elsewhere — so a kPlanConnect tenant gets the tailored experience
  // without anyone toggling it. A workspace override still wins.
  defaultOnInKpc: boolean;
}

export const FEATURE_FLAGS: FeatureFlagDef[] = [
  {
    id: 'census_mapping_fingerprints',
    label: 'Census mapping fingerprints',
    description:
      'Apply a census column mapping one person approved to every client whose census carries the same source and the same header set, with no second confirmation. A file whose header set leaves any mapped column contested, or whose stated totals disagree with its own rows, still parks for review. Off = each client approves its own mapping, and an approval granted while it is off is never reused.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'kpc_cobranding',
    label: 'KPC co-branding',
    description:
      'KPlanConnect demo mode: co-branding next to Matilda in the navbar, plus a payroll-focused tailoring of the app — trimmed Clients list, payroll-first client view, and staff/dev surfaces (fact card, Shuttle link, admin launcher, integration settings) hidden.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: true,
  },
  {
    id: 'partners',
    label: 'Partners',
    description:
      'Advisor + CPA referral dashboards, the /partners workspace, the CRM Partners panel, and Matilda magical compose.',
    defaultOn: true,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'kpc_branding',
    label: 'kPlanConnect co-branding',
    description:
      'Show the "× Kplan connect" lockup in the navbar, the same lockup on the partner-portal chrome, and "KPlanConnect" copy on the expired-link page. Turn off for non-KPC demos.',
    defaultOn: true,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_auto_transport',
    label: 'Schwab payroll auto-transport',
    description:
      'Carry a built payroll file to the Citrix J: drive automatically via the Ubuntu computer-use host. Off = the cycle rests at built with a Today card asking for a manual carry (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_auto_nod',
    label: 'Schwab payroll auto-NOD',
    description:
      'Upload the daily NOD file to the trust bank automatically once it has been generated and reconciled. Uploading only stages the file — SRC validates the format and nothing is pulled. Off = the file is still generated and reconciled, and a Today card points a human at the Upload to SRC button (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_auto_nod_submit',
    label: 'Schwab payroll auto-NOD submit',
    description:
      'Put the NOD file into SRC with Upload & Submit, which is the press that actually requests the ACH pull from employer bank accounts. Off = the file is generated and proven but never reaches SRC, with a Today card asking a human to review the totals and send it (the default everywhere) — SRC has no save-now-submit-later, so there is nothing to leave half-done. After a flip, files already waiting are sent at the next sweep run (or Run sweep now) — same-business-day files only; an older one is failed as stale and regenerated fresh. Nothing pulls money until this is on.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_auto_submit',
    label: 'Schwab payroll auto-submit',
    description:
      'Submit a transported payroll file to the SchARP API automatically. Off = the cycle rests at transported with a Today card asking for a manual submit click (the default everywhere). Nothing trades until launch either way.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_auto_launch',
    label: 'Schwab payroll auto-launch',
    description:
      'Launch a Schwab payroll for trading automatically once it parks after a clean submit, instead of waiting for a manual launch click on the admin Schwab payroll page. Off = manual launch (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'shuttle_auto_acquire',
    label: 'Shuttle auto-acquire',
    description:
      'Pull the weekly census file batch from the Shuttle API automatically (Monday-morning cron) instead of waiting for a manual zip upload. Off = manual upload (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'adp_run_auto_acquire',
    label: 'ADP RUN auto-acquire',
    description:
      'Pull ADP RUN payroll on the ingestion schedule via an unattended console pull using the saved sign-in and its bound authenticator. Off = the payroll console stays the manual pull path (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'gusto_auto_acquire',
    label: 'Gusto auto-acquire',
    description:
      'Pull Gusto payroll on the ingestion schedule via an unattended console pull using the saved sign-in and its bound authenticator. Off = the payroll console stays the manual pull path (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_halt',
    label: 'Schwab payroll kill switch',
    description:
      'Halt every Schwab payroll submission and launch across the workspace. Checked between every step — flip on to stop the pipeline dead.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'srt_setup_halt',
    label: 'SRT setup kill switch',
    description:
      'Halt SRT Plan Maintenance setup sessions. Checked before each plan session and between items — flip on to stop the sweep dead.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'srt_setup_paused',
    label: 'SRT setup paused',
    description:
      'Pause the SRT sweep so a human can use Citrix: the worker finishes its current item, releases the operator session, and leaves remaining plans to resume. Unlike the kill switch, resuming picks up where it left off.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'missing_data_chase',
    label: 'Missing-data chase',
    description:
      'Chase the sponsor for the employee details the recordkeeper cannot open an account without: one Today card per employer, a drafted email to approve, and a follow-up on the same thread once the deposit deadline tightens. Nothing is ever sent without an approval either way. Off = the gap is still detected and still filled silently from an approved census where we already hold the value, but no card and no draft are minted (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'hide_unavailable',
    label: 'Hide unavailable integrations',
    description:
      'Hide integrations whose external credentials/egress are not provisioned in this environment (Zoho, HubSpot, FTWilliam, DocuSign, AgentMail provisioning) so they cannot be reached from the UI. Off everywhere by default — flip on for demo/pentest workspaces that should not surface unconfigured connectors.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
];

function defaultFor(def: FeatureFlagDef, isKpc: boolean): boolean {
  if (isKpc && def.defaultOnInKpc) return true;
  if (isKpc && def.hiddenByDefaultInKpc) return false;
  return def.defaultOn;
}

export function defaultFlagsFor(isKpc: boolean): WorkspaceFeatureFlags {
  return Object.fromEntries(
    FEATURE_FLAGS.map(def => [def.id, defaultFor(def, isKpc)]),
  ) as WorkspaceFeatureFlags;
}

// The full effective config: stored per-workspace overrides win over the
// code-computed defaults.
export function resolveFlags(
  isKpc: boolean,
  overrides: Partial<WorkspaceFeatureFlags>,
): WorkspaceFeatureFlags {
  return { ...defaultFlagsFor(isKpc), ...overrides };
}
